import type { CsvDelimiter } from './types';

/**
 * Séparateur RÉEL des fichiers déposés.
 *
 * Nos modèles sont en « ; » (le défaut de la configuration), mais un export
 * Google Sheets — ou un « Save as CSV » anglo-saxon — est séparé par des
 * VIRGULES. Lu avec le mauvais séparateur, le fichier n'a plus qu'une seule
 * colonne, nommée avec la ligne d'en-tête entière : le contrôle de cohérence
 * déclare alors absentes toutes les colonnes pourtant présentes (« Colonne
 * « classe » absente… », école START le 24/08/2026) et l'import ne crée rien
 * d'exploitable. Le backend renifle aussi de son côté ({@see CsvUtil}) ; ici
 * on aligne la configuration pour que l'utilisateur VOIE ce qui sera utilisé.
 */

/** Caractère réel de chaque valeur de la config (« \t » y est un littéral). */
const CANDIDATES: Array<{ value: CsvDelimiter; char: string }> = [
  { value: ';', char: ';' },
  { value: ',', char: ',' },
  { value: '\\t', char: '\t' },
  { value: '|', char: '|' },
];

/** Assez pour la ligne d'en-tête, même très large, sans lire tout le fichier. */
const HEADER_BYTES = 64 * 1024;

/**
 * Séparateur majoritaire des fichiers, ou `null` si indéterminé — aucun
 * candidat ne découpe l'en-tête en au moins deux colonnes (fichier
 * légitimement mono-colonne comme `niveaux.csv`, ou fichier vide) : dans ce
 * cas la config n'est pas touchée.
 */
export async function detectCsvDelimiter(files: File[]): Promise<CsvDelimiter | null> {
  const votes = new Map<CsvDelimiter, number>();
  for (const file of files) {
    const detected = await detectFileDelimiter(file);
    if (detected) votes.set(detected, (votes.get(detected) ?? 0) + 1);
  }
  let best: CsvDelimiter | null = null;
  let bestVotes = 0;
  // Ordre des candidats = arbitrage des ex æquo (« ; », celui de nos modèles, gagne).
  for (const { value } of CANDIDATES) {
    const count = votes.get(value) ?? 0;
    if (count > bestVotes) {
      best = value;
      bestVotes = count;
    }
  }
  return best;
}

async function detectFileDelimiter(file: File): Promise<CsvDelimiter | null> {
  let head: string;
  try {
    head = await file.slice(0, HEADER_BYTES).text();
  } catch {
    return null;
  }
  const header = firstRecord(head);
  if (header === '') return null;

  let best: CsvDelimiter | null = null;
  let bestCount = 1;
  for (const { value, char } of CANDIDATES) {
    const count = columnCount(header, char);
    if (count > bestCount) {
      best = value;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Premier ENREGISTREMENT (pas première ligne physique) : une cellule entre
 * guillemets peut contenir des retours à la ligne.
 */
function firstRecord(text: string): string {
  const clean = text.replace(/^\uFEFF/, '');
  let inQuotes = false;
  for (let i = 0; i < clean.length; i++) {
    const c = clean[i];
    if (c === '"') {
      inQuotes = !inQuotes;
    } else if (!inQuotes && (c === '\n' || c === '\r')) {
      return clean.slice(0, i);
    }
  }
  return clean;
}

/** Colonnes NOMMÉES de l'en-tête pour un séparateur donné (RFC 4180). */
function columnCount(header: string, delimiter: string): number {
  const cells: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < header.length; i++) {
    const c = header[i];
    if (inQuotes) {
      if (c === '"' && header[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (c === '"') {
        inQuotes = false;
      } else {
        cell += c;
      }
      continue;
    }
    if (c === '"') {
      inQuotes = true;
    } else if (c === delimiter) {
      cells.push(cell);
      cell = '';
    } else {
      cell += c;
    }
  }
  cells.push(cell);
  // Une colonne sans nom n'existe pas : « a,b, » ne vaut pas 3 colonnes.
  return cells.filter((c) => c.trim() !== '').length;
}
